'use client';

import { logger } from './logger';
import { emitSessionEnd } from './telemetry/session-telemetry';
import {
  type AuthResetReason,
  bypassesFreshnessGuard,
  isTerminalResetReason,
} from './auth-reset-reasons';

// Re-exported for existing import sites (callers import the type from here).
// The reason taxonomy + terminal/transient classification lives in
// ./auth-reset-reasons so the Edge middleware can share it (gh #1007).
export type { AuthResetReason } from './auth-reset-reasons';

interface AuthResetHandlers {
  clearApiTokenCache?: () => void;
  clearQueryCache?: () => void;
}

interface ResetAuthSessionOptions {
  redirectToLogin?: boolean;
  preserveRedirect?: boolean;
}

const authResetHandlers: AuthResetHandlers = {};

let resetPromise: Promise<void> | null = null;
let redirectScheduled = false;
let loggingOut = false;

export function registerAuthResetHandlers(handlers: AuthResetHandlers): void {
  if (handlers.clearApiTokenCache) {
    authResetHandlers.clearApiTokenCache = handlers.clearApiTokenCache;
  }

  if (handlers.clearQueryCache) {
    authResetHandlers.clearQueryCache = handlers.clearQueryCache;
  }
}

export function isAuthResetInProgress(): boolean {
  return resetPromise !== null || redirectScheduled;
}

/**
 * gh #946: was a session (re)established within the freshness window?
 *
 * Reads the non-httpOnly `__session_fresh` marker cookie set by
 * `POST /api/auth/session` (see app/api/auth/session/route.ts). The cookie
 * self-expires (Max-Age 60s), so its mere presence means "a session was
 * established in the last minute." Shared across tabs of this origin, so a
 * still-idle background tab also sees a sibling tab's fresh login.
 *
 * Used to suppress transient auth resets and cross-tab idle re-locks that
 * would otherwise tear down a just-established session (the post-idle-lock
 * re-login bounce loop).
 */
export function wasSessionRecentlyEstablished(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.cookie
    .split(';')
    .some((c) => c.trim().startsWith('__session_fresh='));
}

/**
 * Mark a deliberate app-initiated logout as in progress. Suppresses the
 * 401-reset-to-/login path in api-client + auth-context during the brief
 * window between backend token blocklist and `window.location.href = '/logout'`,
 * where in-flight useQuery polls would otherwise bounce the user to /login.
 * One-shot: cleared automatically on next page load (module re-init).
 */
export function setLoggingOut(value: boolean): void {
  loggingOut = value;
}

export function isLoggingOut(): boolean {
  return loggingOut;
}

export function getAuthResetMessage(reason: AuthResetReason | null): string {
  switch (reason) {
    case 'profile-401':
      return 'Your session became invalid while loading your profile. Please sign in again.';
    case 'api-401-refresh-failed':
      return 'Your session expired or became invalid. Please sign in again.';
    case 'login-session-sync-failed':
      return 'We could not restore your saved session. Please sign in again.';
    case 'manual-user-reset':
      return 'Saved browser session was cleared. Please sign in again.';
    case 'session-expired':
      return 'Your session ended. Please sign in again.';
    case 'no-app-access':
      return 'Your account does not have access to this application. Contact your administrator.';
    default:
      return 'Your browser session was reset. Please sign in again.';
  }
}

export function isInitialResetRecoveryLocation(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const searchParams = new URLSearchParams(window.location.search);
  return (
    window.location.pathname === '/login' &&
    searchParams.get('reset') === '1' &&
    searchParams.get('recovered') !== '1'
  );
}

function buildLoginResetUrl(
  reason: AuthResetReason,
  options: Required<ResetAuthSessionOptions>
): URL | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const loginUrl = new URL('/login', window.location.origin);
  loginUrl.searchParams.set('reset', '1');
  loginUrl.searchParams.set('reason', reason);

  if (options.preserveRedirect && window.location.pathname !== '/login') {
    const currentPath = `${window.location.pathname}${window.location.search}`;
    loginUrl.searchParams.set('redirect', currentPath);
  }

  return loginUrl;
}

function clearSessionStorageState(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem('invitation_pending');
  } catch (error) {
    logger.warn('[AuthReset] Failed to clear sessionStorage state', { error });
  }
}

async function clearCacheStorage(): Promise<void> {
  if (typeof window === 'undefined' || typeof caches === 'undefined') {
    return;
  }

  try {
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
  } catch (error) {
    logger.warn('[AuthReset] Failed to clear Cache Storage', { error });
  }
}

function clearRegisteredCaches(): void {
  try {
    authResetHandlers.clearApiTokenCache?.();
  } catch (error) {
    logger.warn('[AuthReset] Failed to clear API token cache', { error });
  }

  try {
    authResetHandlers.clearQueryCache?.();
  } catch (error) {
    logger.warn('[AuthReset] Failed to clear React Query cache', { error });
  }
}

async function clearClientAuthSession(reason: AuthResetReason): Promise<void> {
  const terminal = isTerminalResetReason(reason);
  logger.warn('[AuthReset] Clearing client auth session', { reason, terminal });

  // Always drop the __session cookie so middleware re-gates the next
  // navigation to /login. ?purge=cookie clears ONLY the cookie (no
  // Clear-Site-Data:"storage"); the terminal browser-storage wipe, when
  // warranted, is applied by middleware.ts on the /login?reset=1 landing,
  // keyed off the same terminal/transient classification (gh #1007).
  if (typeof window !== 'undefined') {
    try {
      await fetch('/api/auth/session?purge=cookie', { method: 'DELETE' });
    } catch (error) {
      logger.warn('[AuthReset] Failed to clear session cookie', { reason, error });
    }
  }

  // CJIS AC-11 / IA-11 (gh #1007): only a TERMINAL reset (idle-lock / 12h cap /
  // explicit user reset / backend no-access refusal) signs the Firebase client out. clearLocalState drops
  // the browserLocalPersistence user record, forcing a fresh interactive
  // re-authentication (email + password + TOTP) — the correct outcome after a
  // real session end. A TRANSIENT reset (profile-401, api-401-refresh-failed,
  // login-session-sync-failed) preserves that record so the /login
  // existing-session sync can silently re-establish the cookie without a
  // redundant password+TOTP round-trip. Silent recovery is still gated:
  // recoverSession()/getIdToken() re-check the 12h cap + second-factor claim,
  // and shared/auth.py re-verifies the bearer server-side on every call, so a
  // genuinely dead session degrades to full re-auth rather than resuming.
  //
  // NOTE: clearLocalState (not signOut). signOut() is the user-initiated
  // full-logout path — it blocklists the bearer, stashes an SSO logout hint,
  // fires its own DELETE, and navigates to /logout — none of which is correct
  // for an automatic auth reset.
  if (terminal) {
    try {
      const { getAuthClient } = await import('./auth-client');
      await getAuthClient().clearLocalState();
    } catch (error) {
      logger.warn('[AuthReset] Failed to clear auth client local state', { reason, error });
    }
  }
}

export async function clearBrowserAuthArtifacts(reason: string): Promise<void> {
  logger.warn('[AuthReset] Clearing browser auth artifacts', { reason });

  clearRegisteredCaches();
  clearSessionStorageState();
  await clearCacheStorage();
  clearRegisteredCaches();
}

export async function resetAuthSession(
  reason: AuthResetReason,
  options: ResetAuthSessionOptions = {}
): Promise<void> {
  if (resetPromise || redirectScheduled) {
    logger.warn('[AuthReset] Reset already in progress', { reason });
    return resetPromise ?? Promise.resolve();
  }

  // gh #946: suppress transient resets against a just-established session.
  // The post-idle-lock bounce loop was a client-initiated DELETE
  // /api/auth/session firing ~35s after each successful re-login (a
  // profile-401 race and/or a stale idle tab), tearing the fresh session
  // down and returning the user to /login. If a session was established
  // within the freshness window, swallow the reset — the underlying query
  // (e.g. /api/auth/me) retries and recovers once the transient clears;
  // if the session is genuinely dead the marker expires (<=60s) and a later
  // reset proceeds. 'manual-user-reset' (explicit user intent) and
  // 'no-app-access' (a server verdict, not a race) bypass this guard — see
  // bypassesFreshnessGuard; 'login-session-sync-failed' never sets the marker
  // (the POST failed) so it is not affected.
  if (!bypassesFreshnessGuard(reason) && wasSessionRecentlyEstablished()) {
    logger.warn('[AuthReset] Suppressing reset — session recently established', {
      reason,
    });
    return;
  }

  // preserveRedirect defaults FALSE (issue #822): resetAuthSession fires when a
  // __session cookie exists but the token/profile is dead — a degenerate state,
  // not the clean "no cookie" deep-link case (that path is handled by
  // middleware.ts, which sets ?redirect=<path> itself). Preserving the current
  // URL here re-sent the user to the same failing/unreachable page after
  // re-login, producing the logout→login→logout loop. Reset always lands on the
  // login default (/dashboard). Callers that genuinely want the pre-reset URL
  // back must opt in explicitly with { preserveRedirect: true }.
  const normalizedOptions: Required<ResetAuthSessionOptions> = {
    redirectToLogin: options.redirectToLogin ?? true,
    preserveRedirect: options.preserveRedirect ?? false,
  };

  // gh #876: this reset is now committed (past the in-progress + freshness
  // guards), so the session is genuinely ending. Emit the telemetry event
  // once, here, so every reset reason is captured exactly once regardless of
  // which of the several reset call sites (api-client 401, auth-context
  // profile-401, providers session-expired, manual reset) triggered it.
  emitSessionEnd(reason);

  resetPromise = (async () => {
    logger.warn('[AuthReset] Resetting auth session', {
      reason,
      redirectToLogin: normalizedOptions.redirectToLogin,
      preserveRedirect: normalizedOptions.preserveRedirect,
    });

    clearRegisteredCaches();
    await clearClientAuthSession(reason);
    await clearBrowserAuthArtifacts(reason);

    const loginUrl = buildLoginResetUrl(reason, normalizedOptions);
    if (!loginUrl || typeof window === 'undefined') {
      return;
    }

    if (normalizedOptions.redirectToLogin) {
      redirectScheduled = true;
      window.location.replace(loginUrl.toString());
      return;
    }

    const nextPath = `${loginUrl.pathname}${loginUrl.search}`;
    const currentPath = `${window.location.pathname}${window.location.search}`;
    if (currentPath !== nextPath) {
      window.history.replaceState(null, '', nextPath);
    }
  })();

  try {
    await resetPromise;
  } finally {
    if (!redirectScheduled) {
      resetPromise = null;
    }
  }
}
